/**
 * ContourEditorController — AK-321 «Редактор контура — обвести поле» v1.1
 * + AK-371 v2 «фигуры-первыми».
 *
 * Mutable-редактор контура на terra-draw select-mode. Отделён от read-only
 * MeasureController (ADR-038): две edit-модели НЕ смешиваются.
 *
 * Изоляция (ADR-038, жёстко):
 *   - terra-draw рендерит в СВОЙ namespace `td-edit-` (adapter prefixId) —
 *     отдельные source/layers, НЕ `fields`/`fields-mvt`/`measure-src`.
 *   - НЕ трогаем registry/LayerManager/setupMapLayers/mapReducer.
 *   - editor-слои НЕ подключены к recreateMvtSource / clearTiles — scope-смена
 *     полей не задевает редактор и наоборот.
 *   - z-order: terra-draw слои добавляются поверх (renderBelowLayerId не задаём),
 *     над NDVI/field-контурами.
 *
 * ── AK-371 v2 модель «фигуры-первыми» (founder-UAT #443) ─────────────────────
 * Редактор держит НАБОР независимых фигур (каждая = отдельная terra-draw
 * Polygon-фича, editable по вершинам). Проблема v1: фигура рисовалась «под
 * операцию» и на mouseup сразу поглощала контур → вторая фигура стирала первую.
 *
 * v2:
 *   1. Три инструмента: Прямоугольник, Круг (2 режима), Полигон (поточечно).
 *      Рисование ДОБАВЛЯЕТ фигуру к набору — ничего не стирается.
 *   2. Фигуры пересекаются → доступны «Вырезать» / «Объединить».
 *   3. «Объединить» → union всех фигур в одно поле (MultiPolygon).
 *   4. «Вырезать» → режим выбора цели: клик по фигуре A = A вычитается из union
 *      остальных (A исчезает; дырка при 100%-вложенности; ПУСТО когда A покрыла
 *      всё → все фигуры удаляются с undo).
 *   5. «Готово» сохраняет ВЕСЬ набор как одно поле-мультиполигон.
 *
 * Набор фигур = non-guidance Polygon-фичи terra-draw (currentMultiPolygon их
 * собирает). История (undo/redo) — снимки ВСЕГО набора (MultiPolygon).
 *
 * Перф (ADR-038): snap-проекции колец кэшируются и пересчитываются только на
 * `moveend`; snap-lookup rAF-троттлится в onMouseMove. drag terra-draw имеет
 * собственный dragEventThrottle. Midpoint-density (пункт 8 спеки v2) —
 * zoom-зависимая видимость через styling-callback (getZoom в замыкании).
 */
import maplibregl from 'maplibre-gl';
import {
  TerraDraw,
  TerraDrawSelectMode,
  TerraDrawRenderMode,
  TerraDrawPolygonMode,
  type GeoJSONStoreFeatures,
} from 'terra-draw';
import { TerraDrawMapLibreGLAdapter } from 'terra-draw-maplibre-gl-adapter';
import { polygonAreaSqm, polygonPerimeterMeters, formatArea, formatDistance } from '../measure/geo';
import type { EditorResult, ShapeTool } from './editorStore';
import type { CircleMode } from '../measure/measureStore';
import { loadFieldGeometry, type FieldGeometry, type LngLat } from './fieldGeojson';
import { SnapIndex, type ScreenPt } from './snap';
import { resolveAmberHex, VERTEX_WHITE } from './earthtech';
import { EditHistory, type GeometrySnapshot } from './editHistory';
import { deleteVertexFromRing, findNearestVertexIndex } from './vertexEdit';
import { isMapAlive } from '../isMapAlive';
import { draftPointsFromSnapshot, draftRingFromSnapshot } from './draftRing';
import {
  type MultiPolygonCoords,
  type Ring,
  type ShapeParts,
  shapesIntersect,
  unionAllShapes,
  cutShapeFromRest,
  validateNoKinks,
  rectangleRing,
  outerRings as mpOuterRings,
  ensureClosed as ensureClosedRing,
} from './geometryOps';
import { circlePolygon, distanceMeters } from '../measure/geo';

/** Namespace всех terra-draw слоёв/source на карте. */
const TD_PREFIX = 'td-edit';
/** Mode-имена terra-draw. */
const MODE_SELECT = 'select';
/**
 * Чем закончилась попытка замкнуть рисуемый полигон по кнопке «Готово».
 * `closed` — контур готов, редактор можно закрывать; остальное — повод
 * сказать человеку, чего не хватает, и остаться в редакторе.
 */
/**
 * AK-590: геометрия контура, как её отдаёт редактор наружу. Один union на все
 * стыки (колбэк изменений, снимок на закрытии, итог для сохранения) — раньше он
 * был выписан заново в четырёх местах контроллера и ещё в двух в v2.tsx.
 */
export type EditorGeometry =
  | { type: 'Polygon'; coordinates: LngLat[][] }
  | { type: 'MultiPolygon'; coordinates: LngLat[][][] };

export type FinishDrawOutcome =
  | 'closed'
  | 'not-drawing'
  | 'too-few-points'
  | 'failed'
  | 'unavailable';

const MODE_RENDER = 'render'; // нерисующий режим для programmatic addFeatures
const MODE_POLYGON = 'polygon'; // AK-373: поточечное рисование контура с нуля
/**
 * Пиксельный порог попадания dblclick по вершине (AK-322 удаление). Близко к
 * snap-порогу (11px), но чуть строже — чтобы дабл-клик «в поле» рядом с
 * вершиной не съедал её случайно.
 */
const VERTEX_HIT_PX = 10;
/**
 * AK-371 v2 пункт 8: минимальный зум, при котором показываем midpoint-ручки на
 * рёбрах. Ниже — прячем (width/opacity → 0), чтобы коротко-рёберный полигон на
 * мелком зуме не превращался в «бусы» из midpoint'ов (founder: «кликнул одну,
 * появились две»). При призуме выше порога midpoint'ы проявляются. Порог
 * подобран так, что на типовом полевом зуме (14+) ручки видны.
 */
const MIDPOINT_MIN_ZOOM = 14;

/**
 * AK-371 v2 — какой примитив рисуем следующим кликом/drag'ом. Рисование
 * ДОБАВЛЯЕТ фигуру к набору. Полигон — поточечный (terra-draw polygon-mode);
 * прямоугольник/круг — drag.
 */
export type { ShapeTool };

export class ContourEditorController {
  private draw: TerraDraw | null = null;
  private snapIndex = new SnapIndex(11);
  private amber = resolveAmberHex();
  private installed = false;
  /**
   * id ПОСЛЕДНЕЙ активной (выбранной) фигуры в наборе — для точечных путей
   * (dblclick-удаление вершины currentRing). В v2 фигур несколько; тут — та,
   * с которой пользователь взаимодействует последней.
   */
  private featureId: string | number | null = null;
  /** Подавление лишних recompute между provisional-изменениями. */
  private rafPending = false;
  /** rAF для snap-фидбэка курсора. */
  private snapRafPending = false;
  /** История правок (undo/redo) — снимки ВСЕГО набора фигур (MultiPolygon). */
  private history = new EditHistory(50);
  /**
   * Идёт programmatic-применение снимка истории (undo/redo). На время применения
   * флаг подавляет запись новой записи в историю из onChange/recompute — иначе
   * undo/redo сами породили бы change-событие и зациклили историю (re-entrancy,
   * грабля из measure-контроллера, ADR-038).
   */
  private applyingHistory = false;

  // ── AK-371 v2 рисование фигур + булевы по клику ──────────────────────────
  /**
   * Активный инструмент рисования ('rectangle'|'circle'|'polygon') или null.
   * Пока задан drag-инструмент (rect/circle) — mousedown стартует drag-рисование;
   * 'polygon' переводит terra-draw в polygon-mode (поточечно).
   */
  private drawTool: ShapeTool | null = null;
  /** Режим круга: от центра / диаметр (край-край). */
  private circleMode: CircleMode = 'center';
  /** Первый угол/центр/край фигуры (screen→lngLat) при drag-рисовании. */
  private shapeAnchor: LngLat | null = null;
  /** Идёт drag-рисование фигуры (между mousedown и mouseup). */
  private shapeDragging = false;
  /** Провизорный id фигуры-превью в terra-draw (стирается на mouseup). */
  private shapePreviewId: string | number | null = null;
  /**
   * Режим «Вырезать» активен: следующий клик по фигуре = вырезать её из
   * остальных. Курсор-подсказка + callbacks.onShapeState(cutMode:true).
   */
  private cutMode = false;

  constructor(
    private map: maplibregl.Map,
    private callbacks: {
      onResult: (r: EditorResult | null) => void;
      onHasFeature: (has: boolean) => void;
      onSnapping: (snapping: boolean) => void;
      onLoading: (loading: boolean) => void;
      onError: (error: string | null) => void;
      /** Доступность undo/redo — для disabled-состояния кнопок панели. */
      onHistoryChange?: (state: { canUndo: boolean; canRedo: boolean }) => void;
      /**
       * AK-334: сырое внешнее кольцо на каждое изменение геометрии (для «+ Поле»
       * → «Готово», где нужен уточнённый контур обратно в форму). onResult даёт
       * только форматированные строки, здесь — координаты. null = фигуры нет.
       */
      onRingChange?: (ring: LngLat[] | null) => void;
      /**
       * AK-371: полная геометрия контура (Polygon | MultiPolygon) на каждое
       * изменение — для сохранения мультиполигона/дырок из редактора в форму.
       * onRingChange даёт только внешнее кольцо первой части (обратная
       * совместимость); здесь — вся геометрия. null = фигуры нет.
       */
      onGeometryChange?: (geometry: EditorGeometry | null) => void;
      /**
       * AK-371 v2: состояние набора фигур — сколько их, доступен ли вырез
       * (пересечение), активен ли режим выреза, активный инструмент. Панель
       * этим управляет доступностью кнопок «Вырезать»/«Объединить» и подсказками.
       */
      onShapeState?: (state: {
        shapeCount: number;
        canCut: boolean;
        cutMode: boolean;
        activeTool: ShapeTool | null;
      }) => void;
    },
  ) {}

  // ── lifecycle ──────────────────────────────────────────────────────────

  /** Включить редактор: terra-draw start, select-mode, слушатели карты. */
  activate(): void {
    if (this.installed) return;
    this.amber = resolveAmberHex();

    const adapter = new TerraDrawMapLibreGLAdapter({
      map: this.map as unknown as maplibregl.Map,
      // Namespace всех td-слоёв (ADR-038 изоляция). НЕ задаём renderBelowLayerId
      // → слои идут поверх field-контуров и NDVI.
      prefixId: TD_PREFIX,
    });

    // AK-371 v2 пункт 8: zoom-зависимая видимость midpoint'ов. terra-draw
    // NumericStyling принимает callback (feature)=>number; читаем текущий зум
    // из замыкания и прячем midpoint'ы (width/opacity 0) ниже порога, чтобы на
    // мелком зуме коротко-рёберный контур не превращался в «бусы».
    const midpointVisible = (): boolean => this.map.getZoom() >= MIDPOINT_MIN_ZOOM;

    const select = new TerraDrawSelectMode({
      // Earth Tech amber-стилизация selected-фигуры/вершин/midpoint'ов.
      styles: {
        selectedPolygonColor: this.amber,
        selectedPolygonFillOpacity: 0.12,
        selectedPolygonOutlineColor: this.amber,
        selectedPolygonOutlineWidth: 2,
        selectedLineStringColor: this.amber,
        selectedLineStringWidth: 2,
        // AK-371 v2 пункт 8: настоящая вершина — КРУПНАЯ и залитая (белая с amber-
        // обводкой), чтобы явно отличаться от полупрозрачного midpoint'а.
        selectionPointColor: VERTEX_WHITE,
        selectionPointOutlineColor: this.amber,
        selectionPointWidth: 6,
        selectionPointOutlineWidth: 2,
        // AK-322/v2: midpoint-handle'ы на серединах рёбер (Google/Yandex-стиль).
        // v2 пункт 8 — заметно МЕНЬШЕ вершины и полупрозрачные (визуальная
        // иерархия: крупная залитая точка = вершина, мелкая бледная = «тут можно
        // добавить»). Плюс zoom-зависимость: ниже порога width/opacity → 0.
        midPointColor: this.amber,
        midPointOutlineColor: VERTEX_WHITE,
        midPointWidth: () => (midpointVisible() ? 3 : 0),
        midPointOutlineWidth: () => (midpointVisible() ? 1 : 0),
        midPointOpacity: () => (midpointVisible() ? 0.45 : 0),
      },
      flags: (() => {
        // Snap-хелпер общий для editable-режимов: drag вершины прилипает к
        // границам соседних полей (screen-space, vertex>edge, порог 11px).
        const snappable = {
          toCustom: (event: { containerX: number; containerY: number }) => {
            const cursor: ScreenPt = { x: event.containerX, y: event.containerY };
            const hit = this.snapIndex.snap(cursor);
            this.callbacks.onSnapping(!!hit);
            return hit ? (hit.lngLat as [number, number]) : undefined;
          },
        };
        const editable = {
          feature: {
            draggable: true,
            coordinates: {
              draggable: true,
              midpoints: { draggable: true },
              deletable: true,
              snappable,
            },
          },
        };
        // Полигон поля (обведённый контур): drag фигуры, drag вершин,
        // midpoint-insert, удаление вершин (terra-draw держит >3 для polygon).
        // AK-384: те же edit-флаги дублируем на MODE_POLYGON — defense-in-depth,
        // если фигуру после polygon-draw выберут до нормализации к MODE_RENDER,
        // ручки всё равно появятся (TerraDrawSelectMode ключует флаги по
        // feature.mode; без записи под polygon select давал бы 0 вершин).
        return {
          [MODE_RENDER]: editable,
          [MODE_POLYGON]: editable,
        };
      })(),
      // Сами навешиваем delete на Backspace/Delete (terra-draw default).
      dragEventThrottle: 16,
    });

    // RenderMode — нерисующий режим, куда programmatic addFeatures кладёт
    // геометрию поля. select-mode оперирует уже добавленной фигурой.
    const render = new TerraDrawRenderMode({
      modeName: MODE_RENDER,
      styles: {
        polygonOutlineColor: this.amber,
        polygonOutlineWidth: 2,
        polygonFillColor: this.amber,
        polygonFillOpacity: 0.1,
      },
    });

    // AK-373: поточечный polygon-draw для «Нарисовать» с нуля. Клик = точка,
    // клик по первой точке (или double-click) замыкает контур. Amber-стиль
    // как у select/render — единый визуальный язык редактора.
    const polygon = new TerraDrawPolygonMode({
      styles: {
        fillColor: this.amber,
        fillOpacity: 0.12,
        outlineColor: this.amber,
        outlineWidth: 2,
        closingPointColor: VERTEX_WHITE,
        closingPointOutlineColor: this.amber,
        closingPointWidth: 5,
        closingPointOutlineWidth: 2,
      },
      // Явно фиксируем клавиши завершения/отмены, хотя это и значения по
      // умолчанию. От `finish: 'Enter'` зависит `finishDrawing()`: кнопка
      // «Готово» замыкает контур, отправляя ровно такое нажатие на канву.
      // Оставь мы это на дефолте библиотеки — смена дефолта в апгрейде
      // terra-draw тихо сломала бы кнопку, и заметили бы только по жалобе
      // «поле опять пропало».
      keyEvents: { finish: 'Enter', cancel: 'Escape' },
    });

    this.draw = new TerraDraw({ adapter, modes: [select, render, polygon] });
    this.draw.start();
    this.draw.setMode(MODE_SELECT);

    // Пересчёт результата на любое изменение геометрии (drag/insert/delete).
    this.draw.on('change', this.onChange);
    this.draw.on('deselect', this.onDeselect);
    // AK-373: контур дорисован в polygon-mode → переводим в select для правки
    // (drag/insert/delete/snap) и фиксируем featureId.
    this.draw.on('finish', this.onPolygonFinish);

    // Snap-инфраструктура: проекции колец пересчитываем на moveend, фидбэк —
    // на mousemove (rAF-троттл).
    this.map.on('moveend', this.onMoveEnd);
    this.map.on('mousemove', this.onMouseMove);
    // AK-371 v2: клик по карте в режиме «Вырезать» = выбор фигуры-цели.
    this.map.on('click', this.onMapClick);
    // AK-322: dblclick по вершине → удалить её (founder-жест). Вешаем ДО
    // прочих, чтобы preventDefault успел подавить нативный double-click-zoom
    // когда попали в вершину.
    this.map.on('dblclick', this.onDblClick);
    // v2 пункт 8: midpoint-видимость зависит от зума → форсим перерисовку
    // terra-draw guidance-фич на zoomend (иначе callback не пересчитается).
    this.map.on('zoomend', this.onZoomEnd);
    window.addEventListener('keydown', this.onKeyDown);

    this.installed = true;
    // Наполнить snap-кандидаты видимыми полями сразу при включении.
    void this.refreshSnapFromViewport();
    this.emitShapeState();
  }

  /**
   * Подгрузить геометрии видимых полей (snap-кандидаты). Спрашивает MapLibre за
   * field-id'ы в вьюпорте через скрытый wide click-target слой (`field-click-
   * target`, всегда жив, покрывает поля), затем грузит их полную геометрию
   * (кэш на сессию) и кладёт в SnapIndex. Дёшево — большинство уже в кэше.
   */
  async refreshSnapFromViewport(): Promise<void> {
    let ids: string[] = [];
    try {
      // AK-460: на карточке поля слоя `field-click-target` нет (это слой /map).
      // Без этой проверки MapLibre сыпал в консоль «layer does not exist» на
      // каждый moveend — ошибка ловилась, но мусорила логами.
      if (!this.map.getLayer('field-click-target')) return;
      const feats = this.map.queryRenderedFeatures(undefined, {
        layers: ['field-click-target'],
      });
      ids = Array.from(
        new Set(
          feats
            .map((f) => (f.properties as { id?: string } | undefined)?.id)
            .filter((id): id is string => !!id),
        ),
      );
    } catch {
      // Слой ещё не готов / другой стиль — snap просто без кандидатов.
      return;
    }
    if (!ids.length) return;
    const geoms: FieldGeometry[] = [];
    for (const id of ids) {
      try {
        geoms.push(await loadFieldGeometry(id));
      } catch {
        // одно поле без геометрии — пропускаем, остальные снапим.
      }
    }
    if (geoms.length) this.setSnapCandidates(geoms);
  }

  /**
   * AK-590: геометрия, снятая ИЗ terra-draw в момент закрытия редактора, ДО
   * teardown. Читается через `takeFinalGeometry()`; см. deactivate().
   */
  private finalGeometry: EditorGeometry | null = null;

  /**
   * AK-590: забрать геометрию, с которой редактор закрылся (и обнулить её).
   *
   * Единственный СИНХРОННЫЙ источник правды для перехода «Готово» → форма
   * сохранения. Раньше вызывающий (v2.tsx) собирал контур из React-рефов,
   * которые наполняются АСИНХРОННЫМИ колбэками (`onGeometryChange` идёт из
   * `recompute`, а тот на изменениях геометрии вызывается через
   * requestAnimationFrame). Сама же геометрия живёт только внутри terra-draw и
   * уничтожается синхронно здесь же, в `deactivate()`. Любой порядок, в котором
   * отложенный `recompute` не успел до закрытия, оставлял рефы пустыми — и
   * «Готово» молча стирало нарисованное поле (Safari, v1.1599).
   */
  takeFinalGeometry(): EditorGeometry | null {
    const g = this.finalGeometry;
    this.finalGeometry = null;
    return g;
  }

  /** Полностью выключить редактор: terra-draw stop, снять слушатели. */
  deactivate(): void {
    if (!this.installed) return;
    // AK-590: СНАЧАЛА снимаем геометрию из terra-draw — ниже он будет
    // остановлен (`stop()` + `draw = null`), и восстановить её будет неоткуда.
    // Салвэйдж незамкнутого черновика (≥3 поставленных точек) — тем же путём,
    // что «Готово» замыкает контур: работу человека нельзя терять из-за того,
    // КАК именно закрылся редактор.
    this.finalGeometry = this.snapshotFinalGeometry();
    // AK-371: если инструмент рисования активен на момент закрытия — снять его
    // перехваты drag и вернуть dragPan карте (иначе panning останется выключен).
    if (this.drawTool) this.endDrawTool();
    this.cutMode = false;
    this.map.off('moveend', this.onMoveEnd);
    this.map.off('mousemove', this.onMouseMove);
    this.map.off('click', this.onMapClick);
    this.map.off('dblclick', this.onDblClick);
    this.map.off('zoomend', this.onZoomEnd);
    window.removeEventListener('keydown', this.onKeyDown);
    if (this.draw) {
      this.draw.off('change', this.onChange);
      this.draw.off('deselect', this.onDeselect);
      this.draw.off('finish', this.onPolygonFinish);
      // stop() очищает store И дерегистрирует adapter (убирает td-* слои/source).
      //
      // P0 (AK-343): terra-draw's stop() internally calls
      // `map.removeLayer`/`map.removeSource` on its `td-edit-*` layers. During
      // MapPageV2 unmount that can run after another cleanup already called
      // `map.remove()` (nulled style) → those throw `Cannot read properties of
      // null` → React error boundary «Что-то пошло не так». When the map is
      // gone, remove() already freed terra-draw's layers, so we only need to
      // drop the reference. Guard + try/catch (terra-draw is third-party; its
      // internal teardown path is outside our control).
      if (isMapAlive(this.map)) {
        try {
          this.draw.stop();
        } catch {
          // terra-draw teardown raced map removal — nothing left to clean up.
        }
      }
      this.draw = null;
    }
    this.featureId = null;
    this.history.reset();
    this.installed = false;
    this.callbacks.onHasFeature(false);
    this.callbacks.onResult(null);
    // AK-590: последнее слово редактора на выходе — НАРИСОВАННЫЙ КОНТУР, а не
    // null. Раньше здесь безусловно летели нули, и единственным носителем
    // геометрии оставались рефы вызывающего, наполняемые отложенными
    // колбэками. Теперь порядок не важен: кто слушает — получит контур ровно
    // в момент закрытия. Пусто (ничего не рисовали) — по-прежнему null.
    const outer = this.finalGeometry
      ? ((this.finalGeometry.type === 'Polygon'
          ? this.finalGeometry.coordinates[0]
          : this.finalGeometry.coordinates[0]?.[0]) as LngLat[] | undefined)
      : undefined;
    this.callbacks.onRingChange?.(outer && outer.length >= 4 ? outer : null);
    this.callbacks.onGeometryChange?.(this.finalGeometry);
    this.callbacks.onSnapping(false);
    this.emitHistoryState();
    this.emitShapeState();
  }

  /**
   * AK-590: геометрия «как есть» на момент закрытия редактора, снятая прямо из
   * terra-draw (без рефов и без отложенного recompute).
   *
   * Замкнутые фигуры набора — приоритет. Если их нет, но человек рисовал и
   * поставил ≥3 точек, замыкаем черновик сами (тот же стандарт ГИС, что и
   * кнопка «Готово», AK-577): контур, который человек видел на экране, не
   * должен пропадать из-за того, каким путём закрылся редактор.
   */
  private snapshotFinalGeometry(): EditorGeometry | null {
    if (!this.draw) return null;
    const shapes = this.currentShapes();
    if (shapes.length === 1) {
      return { type: 'Polygon', coordinates: shapes[0] as LngLat[][] };
    }
    if (shapes.length > 1) {
      return { type: 'MultiPolygon', coordinates: shapes as LngLat[][][] };
    }
    const draft = draftRingFromSnapshot(this.draw.getSnapshot());
    if (draft && draft.length >= 3) {
      // roundRing — та же точность координат, что у всех прочих путей
      // редактора (loadRing/undo-снимки): салвэйдж не должен рождать кольцо с
      // другим числом знаков.
      return { type: 'Polygon', coordinates: [roundRing(ensureClosed(draft as LngLat[]))] };
    }
    return null;
  }

  // ── «обвести поле» ────────────────────────────────────────────────────────

  /**
   * Preload внешнего кольца геометрии поля как editable feature. Клик по полю
   * на карте передаёт сюда field_id; грузим полную геометрию (НЕ MVT),
   * добавляем в terra-draw, выделяем, считаем площадь.
   */
  async traceField(fieldId: string): Promise<void> {
    if (!this.draw) return;
    this.callbacks.onLoading(true);
    this.callbacks.onError(null);
    try {
      const geom = await loadFieldGeometry(fieldId);
      if (!this.draw) return; // редактор закрыли пока грузили
      if (geom.primaryRing.length < 4) {
        this.callbacks.onError('У поля нет пригодной геометрии.');
        return;
      }
      // Снести предыдущую фигуру (новый трейс заменяет) + сбросить историю:
      // обведённое поле — новый базовый снимок, undo до него (но не «до пустоты»).
      this.draw.clear();
      this.featureId = null;
      this.history.reset();
      this.cutMode = false;

      // КРИТИЧНО: terra-draw валидирует coordinatePrecision (default 9 знаков) и
      // ОТКЛОНЯЕТ фичу с «excessive precision». Backend ST_AsGeoJSON отдаёт полную
      // double-точность → округляем кольцо до 9 знаков перед addFeatures.
      // (Подтверждено spike: без округления addFeatures возвращает valid:false.)
      const ring = roundRing(ensureClosed(geom.primaryRing));
      const added = this.draw.addFeatures([
        {
          type: 'Feature',
          properties: { mode: MODE_RENDER },
          geometry: { type: 'Polygon', coordinates: [ring] },
        } as GeoJSONStoreFeatures,
      ]);
      const valid = added.find((v) => v.valid);
      if (!valid) {
        this.callbacks.onError('Геометрия поля не прошла валидацию редактора.');
        return;
      }
      this.featureId = valid.id ?? null;
      if (this.featureId != null) {
        this.draw.selectFeature(this.featureId, MODE_SELECT);
      }
      // Соседние поля в кэше — кандидаты для snap (наполняем по мере наведения).
      this.snapIndex.addField(geom);
      this.refreshSnapProjections();
      this.callbacks.onHasFeature(true);
      this.recompute();
    } catch {
      this.callbacks.onError('Не удалось загрузить геометрию поля.');
    } finally {
      this.callbacks.onLoading(false);
    }
  }

  /**
   * AK-334 «Tap-to-field»: загрузить ПРОИЗВОЛЬНЫЙ полигон-контур как editable
   * feature (без похода в API за геометрией по id). Используется, когда контур
   * пришёл из детекта границ («+ Поле» → «Редактировать») — редактор тот же, что
   * и для «обвести поле», меняется лишь источник контура.
   *
   * Принимает внешнее кольцо [lng,lat][] (замкнутое или нет — замкнём сами).
   * Возвращает true, если фича добавлена и выделена.
   */
  loadRing(ring: LngLat[]): boolean {
    if (!this.draw) return false;
    if (ring.length < 3) {
      this.callbacks.onError('Контур слишком мал для редактирования.');
      return false;
    }
    // Новый контур заменяет предыдущий + сбрасывает историю (это базовый снимок).
    this.draw.clear();
    this.featureId = null;
    this.history.reset();
    this.cutMode = false;

    const rounded = roundRing(ensureClosed(ring));
    const added = this.draw.addFeatures([
      {
        type: 'Feature',
        properties: { mode: MODE_RENDER },
        geometry: { type: 'Polygon', coordinates: [rounded] },
      } as GeoJSONStoreFeatures,
    ]);
    const valid = added.find((v) => v.valid);
    if (!valid) {
      this.callbacks.onError('Контур не прошёл валидацию редактора.');
      return false;
    }
    this.featureId = valid.id ?? null;
    if (this.featureId != null) {
      this.draw.selectFeature(this.featureId, MODE_SELECT);
    }
    this.refreshSnapProjections();
    this.callbacks.onHasFeature(true);
    this.recompute();
    return true;
  }

  /**
   * AK-334: снимок текущего внешнего кольца редактируемой фигуры ([lng,lat][],
   * замкнутое). null, если фигуры нет. Нужно, чтобы «Готово» в режиме «+ Поле»
   * вернуло уточнённый контур в форму сохранения.
   */
  currentRing(): LngLat[] | null {
    const poly = this.currentPolygon();
    if (!poly || poly.geometry.type !== 'Polygon') return null;
    const ring = poly.geometry.coordinates[0] as LngLat[];
    return ring.length >= 4 ? ring : null;
  }

  /**
   * Замкнуть полигон, который человек рисует прямо сейчас («Готово» с
   * незамкнутым контуром). Возвращает `false`, если замыкать нечего —
   * поставлено меньше трёх точек.
   *
   * Почему через синтетический Enter, а не прямым вызовом: у terra-draw
   * `PolygonMode.close()` приватный, а сам объект режима контроллер не
   * держит — но у режима есть штатный `keyEvents.finish = 'Enter'`, который
   * адаптер слушает на канве карты. То есть мы идём ровно тем же путём, что
   * и человек, нажавший Enter, и получаем всю библиотечную логику: сброс
   * «резинки» под курсором, снятие `currentlyDrawing`, событие `finish` →
   * `onPolygonFinish` → нормализация фигуры в набор и `recompute()`.
   *
   * Ничего не теряем и при неудаче: если после попытки кольца всё ещё нет,
   * рисунок остаётся на экране, а вызывающий показывает подсказку.
   */
  finishDrawing(): FinishDrawOutcome {
    if (!this.draw) return 'unavailable';

    const points = draftPointsFromSnapshot(this.draw.getSnapshot());
    // Три разных отказа — три разных совета человеку. Одно сообщение на всех
    // означало бы «поставьте 3 точки» тому, кто вообще не начинал рисовать.
    if (!points) return 'not-drawing';
    if (points.length < 3) return 'too-few-points';

    this.map.getCanvas().dispatchEvent(
      new KeyboardEvent('keyup', { key: 'Enter', bubbles: true }),
    );
    return this.currentRing() !== null ? 'closed' : 'failed';
  }

  /** Очистить все фигуры (кнопка «Очистить»). */
  clear(): void {
    if (!this.draw) return;
    if (this.drawTool) this.endDrawTool();
    this.cutMode = false;
    this.draw.clear();
    this.featureId = null;
    this.history.reset();
    this.emitHistoryState();
    this.callbacks.onHasFeature(false);
    this.callbacks.onResult(null);
    this.callbacks.onRingChange?.(null);
    this.callbacks.onGeometryChange?.(null);
    this.emitShapeState();
  }

  /**
   * AK-373 «Нарисовать» с нуля — поточечное рисование контура. Переводим
   * terra-draw в polygon-draw-mode: каждый клик ставит точку, клик по первой
   * точке (или double-click) замыкает контур. По замыканию onPolygonFinish
   * переведёт в select-mode для правки. Заменяет прежний seed-квадрат (#411):
   * пользователь рисует форму поля сам, как в обычном GIS.
   *
   * AK-371 v2: это «Нарисовать с нуля» — сносит набор и стартует новый контур.
   * Для ДОБАВЛЕНИЯ полигон-фигуры к существующему набору см. beginShape('polygon').
   */
  startPolygonDraw(): void {
    if (!this.draw) return;
    // Снести прошлую фигуру (перерисовка с нуля) + сбросить историю.
    this.draw.clear();
    this.featureId = null;
    this.history.reset();
    this.cutMode = false;
    this.emitHistoryState();
    this.callbacks.onError(null);
    this.callbacks.onResult(null);
    this.callbacks.onHasFeature(false);
    this.callbacks.onRingChange?.(null);
    this.callbacks.onGeometryChange?.(null);
    this.emitShapeState();
    this.draw.setMode(MODE_POLYGON);
  }

  // ── AK-371 v2 рисование фигур (добавление к набору) ──────────────────────

  /**
   * Включить инструмент рисования фигуры. Фигура ДОБАВЛЯЕТСЯ к набору (не
   * заменяет — ключевое отличие от v1). rectangle/circle рисуются drag'ом;
   * polygon — поточечно (terra-draw polygon-mode, добавляет фичу к набору).
   *
   * Прямоугольник: drag угол-в-угол. Круг: mode 'center' → mousedown=центр,
   * drag=радиус; mode 'diameter' → mousedown=край, drag=противоположный край
   * (центр=середина, радиус=половина расстояния).
   */
  beginShape(tool: ShapeTool): void {
    if (!this.draw) return;
    // Повторный вызов (пере-выбор инструмента) — сначала снять прошлый перехват.
    if (this.drawTool) this.endDrawTool();
    // Вход в рисование выключает режим «Вырезать» (взаимоисключающие).
    this.cutMode = false;
    this.callbacks.onError(null);
    if (tool === 'polygon') {
      // Поточечный полигон ДОБАВЛЯЕТ фигуру к набору: НЕ чистим draw, просто
      // включаем polygon-mode. onPolygonFinish нормализует новую фичу в набор.
      this.drawTool = 'polygon';
      this.draw.setMode(MODE_POLYGON);
      this.emitShapeState();
      return;
    }
    // rect/circle — свой drag-перехват поверх select-mode (dragPan off на время).
    this.drawTool = tool;
    this.shapeAnchor = null;
    this.shapeDragging = false;
    this.clearShapePreview();
    this.draw.setMode(MODE_SELECT);
    this.map.dragPan.disable();
    // Founder UAT 31.07 (AK-490): «рисование поливных кругов — курсор
    // превращается в руку». Без явного cursor канвас показывает базовый
    // MapLibre `.maplibregl-canvas-container.maplibregl-interactive{cursor:
    // grab}` — он никуда не девается, только dragPan выключен, класс остаётся.
    // crosshair — тот же язык, что уже использует «Вырезать» (toggleCut ниже)
    // и родной terra-draw polygon-mode (свой дефолтный cursor во время
    // поточечного рисования) — единый визуальный язык инструментов рисования.
    this.map.getCanvas().style.cursor = 'crosshair';
    this.map.on('mousedown', this.onShapeDown);
    this.map.on('mousemove', this.onShapeMove);
    this.map.on('mouseup', this.onShapeUp);
    this.emitShapeState();
  }

  /** Задать режим рисования круга (центр / диаметр). */
  setCircleMode(mode: CircleMode): void {
    this.circleMode = mode;
  }

  /**
   * AK-460: снять активный инструмент рисования (повторный клик по кнопке
   * инструмента / «отпустить карту»). Без этого «липкий» инструмент держал
   * `dragPan` выключенным и карта переставала таскаться. Idempotent.
   */
  cancelTool(): void {
    if (!this.drawTool) return;
    this.endDrawTool();
    this.emitShapeState();
  }

  /** Выключить инструмент рисования (снять перехват drag, вернуть dragPan). */
  private endDrawTool(): void {
    this.map.off('mousedown', this.onShapeDown);
    this.map.off('mousemove', this.onShapeMove);
    this.map.off('mouseup', this.onShapeUp);
    if (!this.map.dragPan.isEnabled()) this.map.dragPan.enable();
    // Вернуть базовый cursor (см. beginShape — там выставляем crosshair).
    this.map.getCanvas().style.cursor = '';
    // polygon-mode → вернуть select (если были в нём).
    if (this.draw && this.draw.getMode() === MODE_POLYGON) {
      this.draw.setMode(MODE_SELECT);
    }
    this.drawTool = null;
    this.shapeAnchor = null;
    this.shapeDragging = false;
    this.clearShapePreview();
  }

  private clearShapePreview(): void {
    if (this.draw && this.shapePreviewId != null) {
      try {
        this.draw.removeFeatures([this.shapePreviewId]);
      } catch {
        // превью могло уже уйти — ничего.
      }
    }
    this.shapePreviewId = null;
  }

  private onShapeDown = (e: maplibregl.MapMouseEvent): void => {
    if (!this.drawTool || this.drawTool === 'polygon') return;
    // AK-508 (founder UX-решение, поверх регрессии #603): пока активен режим
    // «Рисовать» (инструмент круга/прямоугольника взведён), рисование имеет
    // АБСОЛЮТНЫЙ приоритет — hit-testing существующих фигур здесь ОТКЛЮЧЁН
    // полностью, любой клик строит НОВУЮ фигуру. #603 пытался угадывать «клик
    // по фигуре = хотим подвинуть» через hitShapeIndex/findVertexHit — на
    // поле, где фигура (референс-контур/зона) покрывает весь вьюпорт, КАЖДЫЙ
    // клик «попадал в фигуру», и рисование не стартовало вообще (AK-508,
    // прод-блокер). Перемещение/правка уже нарисованной фигуры теперь — явный
    // отдельный режим «Выбрать» (см. FieldContourMiniMap: cancelTool()), а не
    // угадывание по координатам клика.
    e.preventDefault();
    this.shapeAnchor = [e.lngLat.lng, e.lngLat.lat];
    this.shapeDragging = true;
  };

  private onShapeMove = (e: maplibregl.MapMouseEvent): void => {
    if (!this.drawTool || this.drawTool === 'polygon') return;
    if (!this.shapeDragging || !this.shapeAnchor || !this.draw) return;
    const ring = this.buildShapeRing(this.shapeAnchor, [e.lngLat.lng, e.lngLat.lat]);
    if (!ring) return;
    // Провизорное превью фигуры (render-mode стиль, стирается на mouseup).
    this.clearShapePreview();
    const added = this.draw.addFeatures([
      {
        type: 'Feature',
        properties: { mode: MODE_RENDER },
        geometry: { type: 'Polygon', coordinates: [roundRing(ring)] },
      } as GeoJSONStoreFeatures,
    ]);
    this.shapePreviewId = added.find((v) => v.valid)?.id ?? null;
  };

  private onShapeUp = (e: maplibregl.MapMouseEvent): void => {
    if (!this.drawTool || this.drawTool === 'polygon' || !this.shapeAnchor) {
      this.endDrawTool();
      return;
    }
    const ring = this.buildShapeRing(this.shapeAnchor, [e.lngLat.lng, e.lngLat.lat]);
    // Убрать превью и снять drag-перехват ДО коммита фигуры.
    this.endDrawTool();
    // AK-508: коммит фигуры сам заканчивает режим «Рисовать» (endDrawTool
    // выше) и НЕ перевзводит инструмент — landing-состояние теперь режим
    // «Выбрать» (нарисовал → сразу можно подвинуть/поправить, founder UX).
    // Ещё одну фигуру того же типа — явный повторный клик по кнопке
    // инструмента в FieldContourMiniMap (sticky-flow AK-460 живёт ТАМ, не
    // здесь: не в контроллере).
    if (ring) this.commitShape(ring);
  };

  /** Построить кольцо фигуры по двум точкам (угол/угол или центр/край/диаметр). */
  private buildShapeRing(anchor: LngLat, cursor: LngLat): Ring | null {
    if (this.drawTool === 'rectangle') {
      return rectangleRing(anchor, cursor);
    }
    if (this.drawTool === 'circle') {
      if (this.circleMode === 'diameter') {
        // anchor и cursor — противоположные точки на окружности (диаметр).
        // Центр = середина, радиус = половина расстояния.
        const center: LngLat = [(anchor[0] + cursor[0]) / 2, (anchor[1] + cursor[1]) / 2];
        const radius = distanceMeters(anchor, cursor) / 2;
        if (radius < 1) return null;
        const ring = circlePolygon(center, radius, 48);
        return ring.length >= 4 ? (ring as Ring) : null;
      }
      // center: anchor = центр, cursor задаёт радиус (геодезически).
      const radius = distanceMeters(anchor, cursor);
      if (radius < 1) return null;
      const ring = circlePolygon(anchor, radius, 48);
      return ring.length >= 4 ? (ring as Ring) : null;
    }
    return null;
  }

  /**
   * AK-371 v2: закоммитить нарисованную фигуру — ДОБАВИТЬ её к набору как
   * отдельную editable-фичу (не заменяя существующие). recompute (не под
   * applyingHistory) запишет снимок набора в историю (undoable).
   */
  private commitShape(ring: Ring): void {
    if (!this.draw) return;
    const rings = [roundRing(ensureClosedRing(ring))];
    const added = this.draw.addFeatures([
      {
        type: 'Feature',
        properties: { mode: MODE_RENDER },
        geometry: { type: 'Polygon', coordinates: rings },
      } as GeoJSONStoreFeatures,
    ]);
    const valid = added.find((v) => v.valid);
    if (!valid) {
      this.callbacks.onError('Фигура не прошла валидацию — попробуйте крупнее.');
      return;
    }
    this.featureId = valid.id ?? this.featureId;
    if (this.featureId != null) {
      try {
        this.draw.selectFeature(this.featureId, MODE_SELECT);
      } catch {
        // фигура не выбралась — не критично, она в наборе.
      }
    }
    this.callbacks.onError(null);
    this.callbacks.onHasFeature(true);
    this.refreshSnapProjections();
    this.recompute();
  }

  // ── AK-371 v2 булевы операции над набором ────────────────────────────────

  /**
   * «Объединить» — union ВСЕХ фигур набора в одно поле (MultiPolygon).
   * Несмежные фигуры остаются частями (это валидно). Заменяет набор на
   * результат union; операция undoable.
   */
  unionShapes(): void {
    if (!this.draw) return;
    if (this.drawTool) this.endDrawTool();
    this.cutMode = false;
    const shapes = this.currentShapes();
    if (shapes.length < 2) {
      // Меньше двух фигур — объединять нечего (уже одно поле).
      this.emitShapeState();
      return;
    }
    const res = unionAllShapes(shapes);
    if (!res.ok) {
      this.callbacks.onError(res.error);
      this.emitShapeState();
      return;
    }
    this.renderParts(res.coords);
    this.callbacks.onError(null);
    this.callbacks.onHasFeature(mpOuterRings(res.coords).length > 0);
    this.refreshSnapProjections();
    this.recompute();
  }

  /**
   * Войти/выйти из режима «Вырезать». В режиме следующий клик по фигуре =
   * вырезать её из остальных (onMapClick → applyCut). Повторный вызов
   * выключает режим. Требует ≥2 фигур (иначе резать не из чего).
   */
  toggleCut(): void {
    if (!this.draw) return;
    if (this.drawTool) this.endDrawTool();
    if (this.cutMode) {
      this.cutMode = false;
      this.emitShapeState();
      return;
    }
    if (this.currentShapes().length < 2) {
      this.callbacks.onError('Нужно минимум две пересекающиеся фигуры.');
      return;
    }
    this.cutMode = true;
    this.callbacks.onError(null);
    this.map.getCanvas().style.cursor = 'crosshair';
    this.emitShapeState();
  }

  /**
   * Клик по карте. В режиме «Вырезать» — определить фигуру под курсором и
   * вырезать её из остальных. Вне режима — no-op (field-click / прочее ведут
   * другие обработчики v2.tsx).
   */
  private onMapClick = (e: maplibregl.MapMouseEvent): void => {
    if (!this.cutMode || !this.draw) return;
    const idx = this.hitShapeIndex(e.point);
    if (idx === null) {
      // Клик мимо фигур — подсказка остаётся, режим не выходим.
      return;
    }
    this.applyCut(idx);
  };

  /**
   * Индекс фигуры набора под экранной точкой (для клика «Вырезать»). Проверяем
   * point-in-polygon по внешнему кольцу каждой фигуры (в порядке набора; при
   * наложении берём ПЕРВУЮ попавшую — верхнюю в наборе). null — мимо всех.
   */
  private hitShapeIndex(point: { x: number; y: number }): number | null {
    const shapes = this.currentShapes();
    const ll = this.map.unproject([point.x, point.y]);
    const pt: LngLat = [ll.lng, ll.lat];
    // Сначала — фигуры, чьё ВНЕШНЕЕ кольцо содержит точку (без учёта дырок для
    // выбора цели: пользователь целится в видимую фигуру).
    for (let i = 0; i < shapes.length; i++) {
      const outer = shapes[i][0];
      if (outer && pointInRing(pt, outer)) return i;
    }
    return null;
  }

  /**
   * Вырезать фигуру `idx` из остальных (cutShapeFromRest). Результат заменяет
   * весь набор. Пустой результат (фигура покрыла всё) → набор очищается
   * (emitted как emptied — панель показывает, что поле стёрто; undo вернёт).
   * Операция undoable.
   */
  private applyCut(idx: number): void {
    if (!this.draw) return;
    const shapes = this.currentShapes();
    const res = cutShapeFromRest(shapes, idx);
    if (!res.ok) {
      this.callbacks.onError(res.error);
      return;
    }
    // Выходим из режима выреза (одна операция за клик).
    this.cutMode = false;
    this.map.getCanvas().style.cursor = '';
    if (res.emptied || res.shapes.length === 0) {
      // Фигура покрыла всё остальное → поле пусто. Рендерим пусто (undo вернёт).
      this.draw.clear();
      this.featureId = null;
      this.callbacks.onHasFeature(false);
      this.callbacks.onError('Вырезано всё поле. Отмените (Ctrl+Z), если это ошибка.');
      // Записать пустой снимок в историю (undoable): recompute запишет null-набор
      // через историю только если набор непуст → пушим руками пустышку не нужно;
      // undo вернёт предыдущий непустой снимок. Обновляем UI и историю.
      this.recompute();
      return;
    }
    this.renderParts(res.shapes as MultiPolygonCoords);
    this.callbacks.onError(null);
    this.callbacks.onHasFeature(mpOuterRings(res.shapes as MultiPolygonCoords).length > 0);
    this.refreshSnapProjections();
    this.recompute();
  }

  /**
   * terra-draw `finish` в polygon-mode: контур замкнут. Забираем свежую фигуру,
   * переводим в select-mode (drag/insert/delete/snap) и фиксируем featureId,
   * чтобы дальнейшая правка и «Готово»-возврат работали как после «Обвести».
   *
   * AK-384 (P1): дорисованная в polygon-mode фигура хранится с `mode:'polygon'`.
   * TerraDrawSelectMode отдаёт selection/vertex/midpoint-ручки ТОЛЬКО если
   * `flags[feature.mode].feature.coordinates` задан — а наши edit-флаги висят на
   * `MODE_RENDER`. Значит select такой фигуры давал 0 ручек: контур замкнут,
   * площадь есть, но вершины не редактируются (симптом тикета). Плюс весь
   * остальной edit-путь (`deleteVertexAt`, `applySnapshot`) пересоздаёт фигуру
   * уже как `MODE_RENDER` — то есть mode рассогласован до первой правки.
   *
   * Фикс: НОРМАЛИЗУЕМ фигуру к `MODE_RENDER` (remove+re-add того же кольца) перед
   * select — единая edit-модель, ручки появляются сразу.
   *
   * AK-371 v2: polygon-draw ДОБАВЛЯЕТ фигуру к набору (draw НЕ чистится) —
   * onPolygonFinish лишь нормализует новую фичу и пересчитывает набор.
   */
  private onPolygonFinish = (
    id: string | number,
    context?: { mode: string; action: string },
  ): void => {
    if (!this.draw) return;
    // finish стреляет и в select-mode (drag/insert вершин) — реагируем ТОЛЬКО
    // на завершение рисования нового контура (action='draw'). Иначе каждый
    // drag вершины сбрасывал бы фигуру в select и ломал правку.
    if (context && context.action !== 'draw') return;
    if (this.draw.getMode() !== MODE_POLYGON) return;
    this.draw.setMode(MODE_SELECT);
    // Инструмент polygon отработал — снять активность (drawTool был 'polygon').
    if (this.drawTool === 'polygon') this.drawTool = null;
    // Забрать кольцо дорисованной фигуры и пере-создать её как MODE_RENDER, чтобы
    // select навесил ручки вершин/midpoint'ы (см. docstring: flags на render).
    this.featureId = this.normalizeToEditable(id);
    if (this.featureId != null) {
      try {
        this.draw.selectFeature(this.featureId, MODE_SELECT);
      } catch {
        // фигура могла не пройти (слишком мало точек) — select-mode без выбора.
      }
    }
    this.callbacks.onHasFeature(true);
    this.refreshSnapProjections();
    this.recompute();
  };

  /**
   * AK-384: заменить фигуру `id` (в любом mode — напр. polygon-draw) на
   * эквивалентную с `mode: MODE_RENDER`, к которому привязаны edit-флаги
   * select-mode (draggable вершин + midpoints + delete + snap). Возвращает id
   * новой editable-фигуры, либо исходный id, если геометрию снять не удалось.
   * НЕ трогает историю — вызывается до её первого снимка (recompute запишет).
   */
  private normalizeToEditable(id: string | number): string | number | null {
    if (!this.draw) return null;
    const snap = this.draw.getSnapshot();
    const feat = snap.find((f) => f.id === id);
    if (!feat || feat.geometry.type !== 'Polygon') return id;
    const props = feat.properties as Record<string, unknown>;
    // Уже editable — ничего не делаем.
    if (props.mode === MODE_RENDER) return id;
    const ring = roundRing(ensureClosed(feat.geometry.coordinates[0] as LngLat[]));
    this.draw.removeFeatures([id]);
    const added = this.draw.addFeatures([
      {
        type: 'Feature',
        properties: { mode: MODE_RENDER },
        geometry: { type: 'Polygon', coordinates: [ring] },
      } as GeoJSONStoreFeatures,
    ]);
    const valid = added.find((v) => v.valid);
    return valid?.id ?? null;
  }

  // ── snap ──────────────────────────────────────────────────────────────────

  /**
   * Зарегистрировать поля-кандидаты для snap (геометрии уже в кэше). Вызывается
   * из v2.tsx когда известны видимые поля.
   */
  setSnapCandidates(geoms: FieldGeometry[]): void {
    this.snapIndex.setFields(geoms);
    this.refreshSnapProjections();
  }

  private refreshSnapProjections(): void {
    if (!this.snapIndex.size) return;
    const b = this.map.getBounds();
    this.snapIndex.reproject(
      (ll: LngLat) => {
        const p = this.map.project({ lng: ll[0], lat: ll[1] });
        return { x: p.x, y: p.y };
      },
      [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()],
    );
  }

  private onMoveEnd = (): void => {
    // Новые поля могли въехать в вьюпорт — подгрузим их геометрии (кэш на сессию,
    // так что повторные moveend по тем же полям бесплатны), затем перепроецируем.
    void this.refreshSnapFromViewport();
    this.refreshSnapProjections();
  };

  /**
   * v2 пункт 8: зум изменился → midpoint-видимость (styling-callback по getZoom)
   * должна пересчитаться. terra-draw пересчитывает стили только на изменение
   * фич — форсим лёгкий re-select текущей фигуры, чтобы guidance перерисовались.
   * rAF-троттлить не нужно: zoomend редкий (по окончании жеста).
   */
  private onZoomEnd = (): void => {
    if (!this.draw || this.featureId == null) return;
    try {
      // Re-select триггерит перестройку guidance-фич (midpoints) с новым зумом.
      this.draw.selectFeature(this.featureId, MODE_SELECT);
    } catch {
      // фигура могла уйти — ничего.
    }
  };

  /**
   * Hover-фидбэк прилипания: показываем индикатор когда курсор у границы поля
   * (до захвата вершины). Сам снап вершины делает toCustom на drag — здесь
   * только UI-подсказка. rAF-троттл, НЕ трогаем cursor (им управляет terra-draw
   * / cutMode).
   */
  private onMouseMove = (e: maplibregl.MapMouseEvent): void => {
    if (this.cutMode || !this.snapIndex.size) return;
    if (this.snapRafPending) return;
    this.snapRafPending = true;
    const cursor: ScreenPt = { x: e.point.x, y: e.point.y };
    requestAnimationFrame(() => {
      this.snapRafPending = false;
      this.callbacks.onSnapping(!!this.snapIndex.snap(cursor));
    });
  };

  /**
   * AK-322: двойной клик/тап по вершине редактируемого полигона удаляет её,
   * контур спрямляется (соседние рёбра смыкаются). По пустому месту — нативный
   * double-click-zoom MapLibre оставляем как есть (НЕ preventDefault).
   *
   * Кроссдевайсность: MapLibre синтезирует `dblclick` и из double-tap на touch
   * (через свои pointer-события), поэтому тот же путь работает на тапе.
   *
   * AK-371 v2: набор из нескольких фигур — ищем фигуру, чья вершина ближе всего
   * к курсору, и удаляем вершину из НЕЁ (не только из первой).
   */
  private onDblClick = (e: maplibregl.MapMouseEvent): void => {
    if (!this.draw || this.applyingHistory || this.cutMode) return;
    const hit = this.findVertexHit(e.point);
    if (!hit) return; // дабл-клик мимо вершины → отдаём нативному зуму
    // Попали в вершину — подавляем double-click-zoom (иначе карта зумится
    // одновременно с удалением).
    e.preventDefault();
    this.deleteVertexAt(hit.featureId, hit.ring, hit.idx);
  };

  /**
   * Найти ближайшую вершину среди ВСЕХ фигур набора к экранной точке. Возвращает
   * фичу, её внешнее кольцо и индекс вершины, либо null.
   */
  private findVertexHit(
    point: { x: number; y: number },
  ): { featureId: string | number; ring: LngLat[]; idx: number } | null {
    if (!this.draw) return null;
    const project = (ll: LngLat) => {
      const p = this.map.project({ lng: ll[0], lat: ll[1] });
      return { x: p.x, y: p.y };
    };
    const cursor: ScreenPt = { x: point.x, y: point.y };
    const snap = this.draw.getSnapshot();
    for (const f of snap) {
      if (f.geometry.type !== 'Polygon' || isGuidance(f) || f.id == null) continue;
      const ring = f.geometry.coordinates[0] as LngLat[];
      const idx = findNearestVertexIndex(ring, cursor, project, VERTEX_HIT_PX);
      if (idx !== null) return { featureId: f.id, ring, idx };
    }
    return null;
  }

  /**
   * Удалить вершину `idx` из кольца фигуры `featureId` через тот же путь, что и
   * undo/redo-снимки (removeFeatures → addFeatures → selectFeature), но БЕЗ
   * applyingHistory — чтобы recompute зафиксировал новый снимок в историю
   * (delete становится undoable: Ctrl+Z вернёт вершину).
   *
   * Guard min-3: если удаление выродило бы полигон (<3 уникальных вершины) —
   * no-op + лёгкий фидбэк через onError.
   */
  private deleteVertexAt(featureId: string | number, ring: LngLat[], idx: number): void {
    if (!this.draw) return;
    const res = deleteVertexFromRing(ring, idx);
    if (!res.ring) {
      if (res.reason === 'min-vertices') {
        this.callbacks.onError('Минимум 3 точки — вершину не удалить.');
      }
      return;
    }
    // Заменить ТОЛЬКО эту фигуру (сохранив её дырки), остальные не трогаем.
    const feat = this.draw.getSnapshot().find((f) => f.id === featureId);
    const holes =
      feat && feat.geometry.type === 'Polygon'
        ? (feat.geometry.coordinates.slice(1) as LngLat[][])
        : [];
    const rings = [res.ring, ...holes.map((h) => roundRing(ensureClosed(h)))];
    this.draw.removeFeatures([featureId]);
    const added = this.draw.addFeatures([
      {
        type: 'Feature',
        properties: { mode: MODE_RENDER },
        geometry: { type: 'Polygon', coordinates: rings },
      } as GeoJSONStoreFeatures,
    ]);
    const valid = added.find((v) => v.valid);
    this.featureId = valid?.id ?? null;
    if (this.featureId != null) {
      this.draw.selectFeature(this.featureId, MODE_SELECT);
    }
    this.callbacks.onError(null);
    this.recompute();
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      // Esc: выйти из режима «Вырезать» / рисования, иначе снять выделение.
      if (this.cutMode) {
        this.cutMode = false;
        this.map.getCanvas().style.cursor = '';
        this.emitShapeState();
        return;
      }
      if (this.drawTool) {
        this.endDrawTool();
        this.emitShapeState();
        return;
      }
      if (this.draw && this.featureId != null) {
        this.draw.deselectFeature(this.featureId);
      }
      return;
    }

    // Undo/redo — только при активном редакторе; не перехватываем, если фокус в
    // поле ввода (чтобы не ломать нативный undo в input/textarea).
    if (!this.installed) return;
    if (isEditableTarget(e.target)) return;
    const mod = e.ctrlKey || e.metaKey;
    if (!mod) return;
    const key = e.key.toLowerCase();
    // Redo: Ctrl/Cmd+Shift+Z или Ctrl+Y. Undo: Ctrl/Cmd+Z (без Shift).
    if ((key === 'z' && e.shiftKey) || key === 'y') {
      e.preventDefault();
      this.redo();
    } else if (key === 'z') {
      e.preventDefault();
      this.undo();
    }
  };

  // ── terra-draw events ────────────────────────────────────────────────────

  private onChange = (): void => {
    if (this.rafPending) return;
    this.rafPending = true;
    requestAnimationFrame(() => {
      this.rafPending = false;
      this.recompute();
    });
  };

  private onDeselect = (): void => {
    // Фигура остаётся, но снято выделение — результат не трогаем.
  };

  // ── результат (площадь/периметр в га) ──────────────────────────────────────

  private recompute(): void {
    if (!this.draw) return;
    // AK-371: собираем ПОЛНУЮ мультиполигон-геометрию из всех фигур-частей
    // terra-draw (каждая editable-фича = одна часть с её дырками).
    const coords = this.currentMultiPolygon();
    if (!coords.length) {
      this.callbacks.onResult(null);
      this.callbacks.onRingChange?.(null);
      this.callbacks.onGeometryChange?.(null);
      this.emitShapeState();
      return;
    }
    // AK-334: сырое ВНЕШНЕЕ кольцо первой части наверх — обратная совместимость
    // с «+ Поле»/import-путём (он пока принимает одно кольцо; мультиполигон-
    // возврат идёт через currentGeometry()).
    const primaryRing = coords[0]?.[0] as LngLat[] | undefined;
    this.callbacks.onRingChange?.(primaryRing && primaryRing.length >= 4 ? primaryRing : null);
    // AK-371: полная геометрия (Polygon одной части / MultiPolygon) наверх — для
    // сохранения дырок/несмежных частей из редактора в форму.
    if (coords.length === 1) {
      this.callbacks.onGeometryChange?.({ type: 'Polygon', coordinates: coords[0] as LngLat[][] });
    } else {
      this.callbacks.onGeometryChange?.({ type: 'MultiPolygon', coordinates: coords as LngLat[][][] });
    }
    // Зафиксировать снимок в историю — НО не во время применения undo/redo
    // (иначе re-entrancy: applySnapshot → change → recompute → push → цикл).
    if (!this.applyingHistory) {
      this.history.push(coords as GeometrySnapshot);
      this.emitHistoryState();
    }
    // Валидная геометрия пересчитана — снять «минимум 3 точки»/прежнюю ошибку
    // (AK-322: min-3 фидбэк транзиентный, исчезает на следующем изменении).
    // НЕ трогаем ошибку в режиме «Вырезать» (там свои подсказки).
    if (!this.cutMode) this.callbacks.onError(null);
    // Площадь/периметр по ВСЕЙ геометрии: площадь суммируется по частям и
    // вычитает дырки (turf area над MultiPolygon), периметр — сумма внешних
    // колец всех частей.
    const areaSqm = multiPolygonAreaSqm(coords);
    let perim = 0;
    let vertexCount = 0;
    for (const part of coords) {
      const outer = part[0];
      if (outer && outer.length >= 4) {
        perim += polygonPerimeterMeters(outer);
        vertexCount += Math.max(0, outer.length - 1);
      }
    }
    const ap = formatArea(areaSqm);
    const partsNote = coords.length > 1 ? ` · ${coords.length} фигур` : '';
    const result: EditorResult = {
      primary: ap.primary,
      secondary: `Периметр ${formatDistance(perim)}${ap.secondary ? ` · ${ap.secondary}` : ''}${partsNote}`,
      vertexCount,
      raw: { areaSqm, perimeterMeters: perim },
    };
    this.callbacks.onResult(result);
    this.emitShapeState();
  }

  // ── undo / redo ────────────────────────────────────────────────────────────

  /** Откатить к предыдущему снимку геометрии. No-op, если откатываться некуда. */
  undo(): void {
    const snap = this.history.undo();
    if (!snap) return;
    this.applySnapshot(snap);
    this.emitHistoryState();
  }

  /** Вернуть отменённую правку. No-op, если redo-стек пуст. */
  redo(): void {
    const snap = this.history.redo();
    if (!snap) return;
    this.applySnapshot(snap);
    this.emitHistoryState();
  }

  /**
   * Применить снимок геометрии к terra-draw: убрать текущие фигуры и добавить
   * набор с теми же координатами, переселектить. На время применения держим
   * applyingHistory=true, чтобы породившийся change не записал снимок обратно в
   * историю (re-entrancy guard). Площадь пересчитываем явно после применения.
   */
  private applySnapshot(coords: GeometrySnapshot): void {
    if (!this.draw) return;
    this.applyingHistory = true;
    try {
      // AK-371: снимок — мультиполигон (набор фигур). Рендерим КАЖДУЮ фигуру
      // отдельной terra-draw фичей; select выделяет первую.
      this.renderParts(coords as unknown as MultiPolygonCoords);
      // Пересчёт — ПОД guard'ом applyingHistory=true, чтобы recompute НЕ записал
      // применённый снимок обратно в историю (иначе push обнулил бы redo-стек).
      this.recompute();
    } finally {
      this.applyingHistory = false;
    }
    this.callbacks.onHasFeature(this.featureId != null);
  }

  /**
   * AK-371: снести все текущие editable-фигуры и заново отрисовать заданный
   * мультиполигон — по одной terra-draw Polygon-фиче на часть/фигуру (с дырками
   * как внутренними кольцами). featureId = первая часть.
   * Возвращает список id добавленных частей.
   */
  private renderParts(coords: MultiPolygonCoords): (string | number)[] {
    if (!this.draw) return [];
    this.draw.clear();
    this.featureId = null;
    const ids: (string | number)[] = [];
    for (const part of coords) {
      const rings = part
        .filter((r) => r && r.length >= 4)
        .map((r) => roundRing(ensureClosed(r as LngLat[])));
      if (!rings.length) continue;
      const added = this.draw.addFeatures([
        {
          type: 'Feature',
          properties: { mode: MODE_RENDER },
          geometry: { type: 'Polygon', coordinates: rings },
        } as GeoJSONStoreFeatures,
      ]);
      const valid = added.find((v) => v.valid);
      if (valid?.id != null) {
        ids.push(valid.id);
        if (this.featureId == null) this.featureId = valid.id;
      }
    }
    if (this.featureId != null) {
      try {
        this.draw.selectFeature(this.featureId, MODE_SELECT);
      } catch {
        // фигура могла не пройти select — оставляем без выделения.
      }
    }
    return ids;
  }

  private emitHistoryState(): void {
    this.callbacks.onHistoryChange?.({
      canUndo: this.history.canUndo,
      canRedo: this.history.canRedo,
    });
  }

  /**
   * AK-371 v2: сообщить панели состояние набора фигур — сколько их, доступен ли
   * вырез (≥2 фигур И они пересекаются), активен ли режим выреза, активный
   * инструмент. «Объединить» панель включает при shapeCount≥2 (склейка валидна
   * и без пересечения); «Вырезать» — при canCut (пересечение).
   */
  private emitShapeState(): void {
    const shapes = this.currentShapes();
    const canCut = shapes.length >= 2 && shapesIntersect(shapes);
    this.callbacks.onShapeState?.({
      shapeCount: shapes.length,
      canCut,
      cutMode: this.cutMode,
      activeTool: this.drawTool,
    });
  }

  /**
   * Текущая редактируемая полигон-фича (не guidance) или null. Для одиночного
   * контура — единственная часть; для набора — первая по порядку.
   */
  private currentPolygon(): GeoJSONStoreFeatures | null {
    if (!this.draw) return null;
    const snap = this.draw.getSnapshot();
    const poly = snap.find((f) => f.geometry.type === 'Polygon' && !isGuidance(f));
    return poly && poly.geometry.type === 'Polygon' ? poly : null;
  }

  /**
   * AK-371 v2: набор фигур-черновиков — каждая non-guidance Polygon-фича
   * terra-draw как ShapeParts (внешнее кольцо + дырки). Порядок — как в снапшоте.
   * Провизорное превью drag-рисования исключаем (оно = shapePreviewId).
   */
  private currentShapes(): ShapeParts[] {
    if (!this.draw) return [];
    const snap = this.draw.getSnapshot();
    const shapes: ShapeParts[] = [];
    for (const f of snap) {
      if (f.geometry.type !== 'Polygon' || isGuidance(f)) continue;
      if (this.shapePreviewId != null && f.id === this.shapePreviewId) continue;
      const rings = (f.geometry.coordinates as LngLat[][]).map((r) => ensureClosedRing(r));
      if (rings[0] && rings[0].length >= 4) shapes.push(rings);
    }
    return shapes;
  }

  /**
   * AK-371: собрать ПОЛНУЮ геометрию поля из всех фигур terra-draw = набор фигур.
   * (Тождественно currentShapes, но именование сохранено для площадь/save-путей.)
   */
  private currentMultiPolygon(): MultiPolygonCoords {
    return this.currentShapes();
  }

  /**
   * AK-371: итоговая геометрия для сохранения — Polygon (одна фигура без дырок)
   * или MultiPolygon (несколько фигур / дырки). null, если фигур нет.
   * Перед возвратом валидирует самопересечения (kinks) — возвращает { error }
   * для блокировки «Готово» (ADR R1/R2).
   */
  currentGeometry():
    | { ok: true; geometry: EditorGeometry }
    | { ok: false; error: string }
    | null {
    const coords = this.currentMultiPolygon();
    if (!mpOuterRings(coords).length) return null;
    const kinkErr = validateNoKinks(coords);
    if (kinkErr) return { ok: false, error: kinkErr };
    if (coords.length === 1) {
      return { ok: true, geometry: { type: 'Polygon', coordinates: coords[0] as LngLat[][] } };
    }
    return { ok: true, geometry: { type: 'MultiPolygon', coordinates: coords as LngLat[][][] } };
  }
}

/**
 * true, если событие клавиатуры пришло из редактируемого поля (input/textarea/
 * contenteditable). Тогда undo/redo редактора не перехватываем — отдаём нативу.
 */
function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
}

/**
 * Округляет координаты кольца до 9 знаков — terra-draw coordinatePrecision
 * (default 9) иначе отклоняет фичу как «excessive precision». 9 знаков ≈ 0.1мм
 * на экваторе, потеря точности агрономически незначима.
 */
const COORD_PRECISION = 1e9;
function roundRing(ring: LngLat[]): LngLat[] {
  return ring.map(
    ([lng, lat]) =>
      [Math.round(lng * COORD_PRECISION) / COORD_PRECISION, Math.round(lat * COORD_PRECISION) / COORD_PRECISION] as LngLat,
  );
}

/** Замыкает кольцо: первая точка в конец, если её там нет. */
function ensureClosed(ring: LngLat[]): LngLat[] {
  const a = ring[0];
  const b = ring[ring.length - 1];
  if (a[0] === b[0] && a[1] === b[1]) return ring;
  return [...ring, a];
}

/** Guidance-фичи terra-draw (midpoint/selectionPoint) помечены в properties. */
function isGuidance(f: GeoJSONStoreFeatures): boolean {
  const p = f.properties as Record<string, unknown>;
  return !!(p.midPoint || p.selectionPoint || p.snappingPoint || p.coordinatePoint);
}

/**
 * Точка внутри кольца (ray-casting, [lng,lat]). Для клика «Вырезать» — определить
 * фигуру под курсором. Кольцо замкнутое или нет — алгоритм не требует замыкания.
 */
function pointInRing(pt: LngLat, ring: LngLat[]): boolean {
  const [x, y] = pt;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * AK-371: геодезическая площадь ВСЕЙ мультиполигон-геометрии, м². Сумма по
 * частям; дырки (внутренние кольца) вычитаются. Используем polygonAreaSqm по
 * кольцам: внешнее (+), каждое внутреннее (−). Совпадает с серверным
 * ST_Area(geography) семантически (source of truth — backend, ADR R3).
 */
function multiPolygonAreaSqm(coords: MultiPolygonCoords): number {
  let total = 0;
  for (const part of coords) {
    for (let i = 0; i < part.length; i++) {
      const ring = part[i];
      if (!ring || ring.length < 4) continue;
      const a = polygonAreaSqm(ring as LngLat[]);
      total += i === 0 ? a : -a; // ring 0 — внешнее (+), 1+ — дырки (−)
    }
  }
  return Math.max(0, total);
}
